import type {
  CombinationLabel,
  CombinationsResponse,
  CreativeCombination,
} from '../models/combinations.types'

import {
  decodeContext,
  decodeCreativeIds,
  numberOrNull,
  percentToRatio,
  record,
  valueOrEmpty,
} from './reportContext'

function decodeLabel(value: unknown): CombinationLabel | null {
  const source = record(value)
  if (!source || typeof source.display !== 'string' || source.display.length === 0) return null
  const rawValue = source.value
  return {
    key: valueOrEmpty(source.key),
    // Falls back to `display` when the backend sends no distinct raw value — equality checks
    // still work (against the same display text), they just can't tell two same-labelled values
    // with different raw payloads apart, an edge case this endpoint hasn't been observed to hit.
    value: rawValue === null || rawValue === undefined ? source.display : valueOrEmpty(rawValue),
    display: source.display,
  }
}

function decodeLabels(value: unknown): CombinationLabel[] {
  return Array.isArray(value)
    ? value.flatMap((label) => {
        const decoded = decodeLabel(label)
        return decoded ? [decoded] : []
      })
    : []
}

function booleanOrNull(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null
}

/** `combo_key` is expected from the backend, but a combo missing it is still shown — its key is
 * rebuilt from its own labels so one malformed field doesn't drop an otherwise-valid row. */
function comboKey(source: Record<string, unknown>, labels: readonly CombinationLabel[]): string {
  const raw = source.combo_key
  if (typeof raw === 'string' && raw.length > 0) return raw
  return labels.map((label) => `${label.key}:${label.display}`).join('|')
}

function decodeMemberDiffPercents(value: unknown): readonly (number | null)[] {
  return Array.isArray(value) ? value.map((entry) => percentToRatio(numberOrNull(entry))) : []
}

function decodeCombination(value: unknown): CreativeCombination | null {
  const source = record(value)
  if (!source) return null
  const labels = decodeLabels(source.labels)
  if (labels.length === 0) return null
  return {
    combo_key: comboKey(source, labels),
    labels,
    sample_size: numberOrNull(source.sample_size) ?? 0,
    metric_value: numberOrNull(source.metric_value),
    diff_percent: percentToRatio(numberOrNull(source.diff_percent)),
    data_status: typeof source.data_status === 'string' ? source.data_status : null,
    is_significant: booleanOrNull(source.is_significant),
    channel: decodeContext(source.channel),
    objective: decodeContext(source.objective),
    campaign_type: decodeContext(source.campaign_type),
    creative_ids: decodeCreativeIds(source.creative_ids),
    member_diff_percents: decodeMemberDiffPercents(source.member_diff_percents),
    baseline_metric_value: numberOrNull(source.baseline_metric_value),
  }
}

function decodeCombinations(value: unknown): CreativeCombination[] {
  return Array.isArray(value)
    ? value.flatMap((combo) => {
        const decoded = decodeCombination(combo)
        return decoded ? [decoded] : []
      })
    : []
}

/**
 * Legacy parity (`isValidCombo`): a combo the backend explicitly flagged (`data_status`) or one
 * with no significance verdict AND a zero metric value (an uncomputed placeholder, not a real
 * zero) never reaches the table.
 */
export function isValidCombination(combo: CreativeCombination): boolean {
  if (combo.data_status) return false
  if (combo.is_significant === null && combo.metric_value === 0) return false
  return true
}

/**
 * Backend compatibility boundary for `.../metadata/creative-ai/combinations/`. Contract 23
 * §BE-23-15 has not locked this DTO — every field is decoded defensively.
 */
export function decodeCombinationsResponse(value: unknown): CombinationsResponse {
  const source = record(value)
  const meta = record(source?.meta)
  return {
    working: decodeCombinations(source?.working).filter(isValidCombination),
    not_working: decodeCombinations(source?.not_working).filter(isValidCombination),
    meta: {
      hiddenBelowSampleSize: numberOrNull(record(meta?.hidden_counters)?.sample_size_below) ?? 0,
    },
    overallValue: numberOrNull(source?.overall_value),
  }
}
